import { environment } from '../../../environments/environment';
import { AppService } from '../../app.service';
import { GlobalService } from '../../global/services/global.service';
import { WorkspaceService } from '../../workspace/workspace.service';
import {
  CeHostDialogData,
  ModalLockPayload,
} from '../../shared/components/ce-host-dialog/ce-host-dialog.component';

/**
 * Per-callName filterParams deriver. Receives the merged filterParams
 * (active-screen filters + grid extraParams + any host overrides) and the
 * row the user clicked, and returns a (possibly new) filterParams object
 * with modal-specific aliases injected.
 */
type FilterParamsDeriver = (
  filterParams: Record<string, any>,
  rowData: any,
) => Record<string, any>;

/**
 * Canonical `/getScreenModal` modal_name + the lowercased aliases that
 * `efile-ctrlfn-dispatcher`, `efile-grid` callbacks, and the legacy
 * AngularJS controllers all flow through. Keep both spellings — the
 * dispatcher uses the canonical form, gizmoGrid `callName` strings are
 * lowercase in some paths.
 */
const XML_VS_ELEMENTS_CALL_NAMES = [
  'xml2elementscompare',
  'xmlvselementscompare',
  'xml2ElementsCompare'.toLowerCase(),
];

/**
 * Single canonical port of the legacy `XmlVsElementsController` /
 * `EfileCtrlFnDispatcherService` rule used by the migrated
 * `XmlVsElementsComponent`:
 *
 *   - if any IRS-id-shaped key already on filterParams (`irs_id`, `IRS_ID`,
 *     `irsId`, `irs_submission_id`, `IRS_SUBMISSION_ID`, `submission_id`,
 *     `SUBMISSION_ID`) — normalise to `irs_id`
 *   - else fall back to the clicked row's `IRS_SUBMISSION_ID` /
 *     `irs_submission_id` / `IRS_ID` / `irs_id` / `IRSSUBMISSION_ID` /
 *     `IRS_SUBMISSIONID`.
 *
 * Broader key list than the dispatcher's previous version — covers both
 * legacy-grid row shapes (uppercased) and migrated CE row payloads
 * (camelCase / mixed).
 */
export function mergeXmlVsElementsIrsIntoFilterParams(
  filterParams: any,
  rowData: any,
): Record<string, any> {
  const fp = { ...(filterParams || {}) };
  const existing =
    fp.irs_id ??
    fp.IRS_ID ??
    fp.irsId ??
    fp.irs_submission_id ??
    fp.IRS_SUBMISSION_ID ??
    fp.submission_id ??
    fp.SUBMISSION_ID;
  if (existing != null && String(existing).trim() !== '') {
    fp.irs_id = String(existing).trim();
    return fp;
  }

  const row = rowData || {};
  const fromRow =
    row.IRS_SUBMISSION_ID ??
    row.irs_submission_id ??
    row.IRS_ID ??
    row.irs_id ??
    row.IRSSUBMISSION_ID ??
    row.IRS_SUBMISSIONID;
  if (fromRow != null && String(fromRow).trim() !== '') {
    fp.irs_id = String(fromRow).trim();
  }
  return fp;
}

/**
 * Per-callName deriver dispatch table. Adding a new modal that needs
 * row→filterParam injection is one entry here, not three edits across
 * dispatcher / directive / component.
 */
const FILTER_PARAM_DERIVERS: Record<string, FilterParamsDeriver> = (() => {
  const table: Record<string, FilterParamsDeriver> = {};
  for (const name of XML_VS_ELEMENTS_CALL_NAMES) {
    table[name] = mergeXmlVsElementsIrsIntoFilterParams;
  }
  return table;
})();

export interface ResolveEfileCeFilterParamsInput {
  workspaceService?: WorkspaceService | null;
  /**
   * Grid-host explicit filter context (e.g. `EfileGridConfig.extraParams`).
   * Wins over `activeScreen.filters` so screen-specific submission_id /
   * type / tax_year context is preserved when the active workspace tab
   * filters are stale or empty.
   */
  gridExtraParams?: Record<string, any> | null;
  callName?: string | null;
  rowData?: any;
}

/**
 * Build the `filterParams` object for a CE modal:
 *   1. Start with `workspaceFactory.activeScreen.filters.getFilterParams()`
 *      when present (may be empty for custom-grid / standalone screens).
 *   2. Merge `gridExtraParams` over the top (host wins over inherited tab
 *      filters — this preserves submission_id / type / tax_year context
 *      that custom efile screens drive themselves).
 *   3. Apply the per-callName deriver (today only `xml2ElementsCompare`
 *      injects `irs_id` from the clicked row).
 */
export function resolveEfileCeFilterParams(
  input: ResolveEfileCeFilterParamsInput,
): Record<string, any> {
  const ws = input.workspaceService;
  const activeScreen = ws?.workspaceFactory?.activeScreen;
  const screenFilters: Record<string, any> = activeScreen?.filters?.getFilterParams
    ? activeScreen.filters.getFilterParams() || {}
    : {};

  const merged: Record<string, any> = {
    ...screenFilters,
    ...(input.gridExtraParams || {}),
  };

  const key = String(input.callName || '').trim().toLowerCase();
  if (key && FILTER_PARAM_DERIVERS[key]) {
    return FILTER_PARAM_DERIVERS[key](merged, input.rowData);
  }
  return merged;
}

/** Resolve `(rowData, colData)` from the two gizmoGrid payload shapes. */
export function resolveEfileCeRowAndCol(
  gridPayload: any,
): { rowDataResolved: any; colDataResolved: any } {
  const isBatchArray = Array.isArray(gridPayload);
  const firstBatchEntry = isBatchArray ? gridPayload[0] : null;
  const rowDataResolved =
    gridPayload?.rowData ?? firstBatchEntry?.data ?? firstBatchEntry ?? {};
  const colDataResolved =
    gridPayload?.colData ?? firstBatchEntry?.colData ?? {};
  return { rowDataResolved, colDataResolved };
}

export interface BuildEfileCeHostDialogDataInput {
  ceTagName: string;
  callName: string;
  /**
   * Raw `_data` from the gizmoGrid callback — either `{ rowData, colData }`
   * or `Array<{ data: <row>, colData?: <col> }>`. Stored on the dialog as
   * `gridData` and parsed for row/col by {@link resolveEfileCeRowAndCol}.
   */
  gridPayload: any;
  workspaceService?: WorkspaceService | null;
  globalService: GlobalService;
  appService: AppService;
  /** `EfileGridConfig.extraParams` when called from the directive. */
  gridExtraParams?: Record<string, any> | null;
  /**
   * Last successful loadGridJson `data.header` (column metadata) — used as
   * the `headerLabel` fallback when `activeScreen.data.header` is empty.
   * `efile-grid.directive` keeps it in `lastLoadedData?.header`.
   */
  fallbackHeader?: any;
  modalDesc?: string;
  lock?: ModalLockPayload;
  templateMode?: string;
}

/**
 * Single source of truth for building a `CeHostDialogData` payload for an
 * efile screen. Mirrors `GridDirective.prepareDialogData` but reads from
 * `gridExtraParams` + `fallbackHeader` so custom-grid screens that don't
 * populate `workspaceFactory.activeScreen.data` still produce a usable
 * payload.
 */
export function buildEfileCeHostDialogData(
  input: BuildEfileCeHostDialogDataInput,
): CeHostDialogData {
  const {
    ceTagName,
    callName,
    gridPayload,
    workspaceService,
    globalService,
    appService,
  } = input;

  const activeScreen = workspaceService?.workspaceFactory?.activeScreen;
  const { rowDataResolved, colDataResolved } = resolveEfileCeRowAndCol(gridPayload);

  const filterParams = resolveEfileCeFilterParams({
    workspaceService,
    gridExtraParams: input.gridExtraParams,
    callName,
    rowData: rowDataResolved,
  });

  const globalParams: Record<string, any> = {
    ...(globalService.global?.globalParams || {}),
  };
  // Custom-screen context envelope: when the launching `EfileGridDirective`
  // resolves these from `workspaceFactory.activeScreen` and folds them into
  // `gridExtraParams` (see `efile-grid.directive.ts:resolveCustomScreenContext`),
  // promote them into `headerData.globalParams` too. Add-edit reads from
  // BOTH `filter-params` AND `header-data` (`buildHierarchyParams.f` /
  // `g`), so populating both routes makes `s3hr77` / `rv8nq0` payloads
  // resilient regardless of which side is in scope when the call fires.
  //
  // `activeScreen.screen_key` is preserved as the deepest fallback (it's
  // the live workspace tab's key) so legacy parity with the old
  // single-key promotion is unchanged.
  const ENV_KEYS = ['activity_key', 'screen_key', 'jcd_ta_key', 'screen_type', 'jcd_key'] as const;
  const extra = (input.gridExtraParams || {}) as Record<string, any>;
  for (const key of ENV_KEYS) {
    const upper = key.toUpperCase();
    const candidate =
      extra[key] ??
      extra[upper] ??
      (activeScreen as any)?.[key] ??
      (activeScreen as any)?.[upper] ??
      (activeScreen as any)?.parent?.[key] ??
      (activeScreen as any)?.parent?.[upper] ??
      globalParams[key] ??
      globalParams[upper];
    if (candidate !== undefined && candidate !== null && String(candidate).trim() !== '') {
      globalParams[key] = candidate;
    }
  }
  if (activeScreen?.screen_key && !globalParams.screen_key) {
    globalParams.screen_key = activeScreen.screen_key;
  }

  const headerData = {
    globalParams,
    userSettingsData: appService.USER_SETTINGS || {},
    serverConfigData: appService.SERVER_CONFIG || {},
    generalConfigData: { ...environment },
  };

  const baseUrls = {
    api: environment.base_url,
    admin: environment.admin_api_url,
    custom: environment.custom_api_url,
    efile: environment.efile_api_url,
    pdfEngine: environment.pdf_engine_api_url,
    bulkPdfEngine: environment.bulk_pdf_engine_api_url,
  };

  const headerLabel = activeScreen?.data?.header ?? input.fallbackHeader ?? null;

  return {
    ceTagName,
    rowData: rowDataResolved,
    colData: colDataResolved,
    headerData,
    baseUrls,
    filterParams,
    gridData: gridPayload,
    modalName: callName || colDataResolved?.call_back || '',
    modalDesc: input.modalDesc,
    lock: input.lock,
    headerLabel,
    templateMode: input.templateMode,
  };
}
